import { i18nService } from '../../../i18n/I18nService';
import { fetchHourlyRates, updateHourlyRate } from '../../../api/adminApi';
import { t, createField, createScreenHeader, createBackButton, isUnauthorized } from './context';
export function showHourlyPaymentScreen(ctx) {
    ctx.screen.innerHTML = '';
    const header = createScreenHeader(t('admin.hourlyPayment.title'));
    const content = document.createElement('div');
    content.className = 'admin__content';
    content.textContent = t('admin.hourlyPayment.loading');
    const backButton = createBackButton(ctx, t('admin.hourlyPayment.back'));
    ctx.screen.append(header, content, backButton);
    loadHourlyRatesContent(ctx, content);
}
function loadHourlyRatesContent(ctx, content) {
    fetchHourlyRates()
        .then((places) => renderHourlyPaymentContent(ctx, content, places))
        .catch((err) => {
        if (isUnauthorized(err)) {
            ctx.showLogin();
            return;
        }
        console.error('Failed to load hourly rates:', err);
        content.textContent = t('admin.hourlyPayment.loadError');
    });
}
function renderHourlyPaymentContent(ctx, content, places) {
    content.innerHTML = '';
    const table = document.createElement('table');
    table.className = 'admin__table';
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const headLabels = [
        t('admin.hourlyPayment.table.name'),
        t('admin.hourlyPayment.table.pricePerHour'),
        t('admin.hourlyPayment.table.active'),
        '',
    ];
    for (const label of headLabels) {
        const th = document.createElement('th');
        th.textContent = label;
        headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);
    const locale = i18nService.getLocale();
    const tbody = document.createElement('tbody');
    for (const place of places) {
        const row = document.createElement('tr');
        row.dataset.placeId = String(place.id);
        const nameCell = document.createElement('td');
        nameCell.textContent = place.name[locale] ?? place.name.ru;
        const priceCell = document.createElement('td');
        priceCell.appendChild(buildPriceTiers(place));
        const activeCell = document.createElement('td');
        activeCell.textContent = place.isActive
            ? t('admin.hourlyPayment.table.activeYes')
            : t('admin.hourlyPayment.table.activeNo');
        const actionsCell = document.createElement('td');
        const editButton = document.createElement('button');
        editButton.type = 'button';
        editButton.className = 'admin__table-edit';
        editButton.textContent = t('admin.hourlyPayment.table.edit');
        editButton.addEventListener('click', () => openHourlyRateModal(ctx, place, content));
        actionsCell.appendChild(editButton);
        row.append(nameCell, priceCell, activeCell, actionsCell);
        tbody.appendChild(row);
    }
    table.appendChild(tbody);
    content.appendChild(table);
}
// Empty input clears the tier (null); anything else must be a non-negative
// number. `undefined` signals a validation failure to the caller.
function parsePriceInput(raw) {
    const trimmed = raw.trim();
    if (trimmed === '')
        return null;
    const num = Number(trimmed);
    return Number.isFinite(num) && num >= 0 ? num : undefined;
}
// One row per price tier (1h / 3h / day), skipping tiers the admin hasn't set.
function buildPriceTiers(place) {
    const wrapper = document.createElement('div');
    wrapper.className = 'admin__price-tiers';
    const tiers = [
        { value: place.pricePerHour, labelKey: 'admin.hourlyPayment.table.hourLabel' },
        { value: place.pricePer3Hours, labelKey: 'admin.hourlyPayment.table.threeHoursLabel' },
        { value: place.pricePerDay, labelKey: 'admin.hourlyPayment.table.dayLabel' },
    ];
    const set = tiers.filter((tier) => tier.value != null);
    if (set.length === 0) {
        wrapper.textContent = t('admin.hourlyPayment.table.null');
        return wrapper;
    }
    for (const tier of set) {
        const row = document.createElement('div');
        row.className = 'admin__price-tier';
        const label = document.createElement('span');
        label.className = 'admin__price-tier-label';
        label.textContent = t(tier.labelKey);
        const value = document.createElement('span');
        value.textContent = `${tier.value.toFixed(2)} €`;
        row.append(label, value);
        wrapper.appendChild(row);
    }
    return wrapper;
}
function openHourlyRateModal(ctx, place, content) {
    const overlay = document.createElement('div');
    overlay.className = 'admin__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = 'admin__modal';
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'admin__modal-close';
    closeButton.textContent = '×';
    closeButton.setAttribute('aria-label', t('admin.hourlyPayment.modal.closeLabel'));
    closeButton.addEventListener('click', () => overlay.remove());
    const locale = i18nService.getLocale();
    const title = document.createElement('h2');
    title.className = 'admin__modal-title';
    title.textContent = place.name[locale] ?? place.name.ru;
    const form = document.createElement('form');
    form.className = 'admin__form';
    const priceField = createField(t('admin.hourlyPayment.modal.priceLabel'), 'number', place.pricePerHour != null ? String(place.pricePerHour) : '');
    const price3HoursField = createField(t('admin.hourlyPayment.modal.price3HoursLabel'), 'number', place.pricePer3Hours != null ? String(place.pricePer3Hours) : '');
    const priceDayField = createField(t('admin.hourlyPayment.modal.priceDayLabel'), 'number', place.pricePerDay != null ? String(place.pricePerDay) : '');
    for (const field of [priceField, price3HoursField, priceDayField]) {
        field.input.min = '0';
        field.input.step = '0.01';
        // Unlike most admin form fields, these are independently optional — a
        // blank tier just means "not set", not a validation error, and
        // createFormField() marks every field required by default.
        field.input.required = false;
    }
    const error = document.createElement('p');
    error.className = 'admin__error';
    error.hidden = true;
    const actions = document.createElement('div');
    actions.className = 'admin__modal-confirm-actions';
    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'cta admin__submit';
    cancelButton.textContent = t('admin.hourlyPayment.modal.cancelLabel');
    cancelButton.addEventListener('click', () => overlay.remove());
    const saveButton = document.createElement('button');
    saveButton.type = 'submit';
    saveButton.className = 'cta admin__submit';
    saveButton.textContent = t('admin.hourlyPayment.modal.saveLabel');
    actions.append(cancelButton, saveButton);
    form.append(priceField.wrapper, price3HoursField.wrapper, priceDayField.wrapper, error, actions);
    form.addEventListener('submit', (event) => {
        event.preventDefault();
        error.hidden = true;
        const hourly = parsePriceInput(priceField.input.value);
        const threeHours = parsePriceInput(price3HoursField.input.value);
        const day = parsePriceInput(priceDayField.input.value);
        if (hourly === undefined || threeHours === undefined || day === undefined) {
            error.textContent = t('admin.hourlyPayment.modal.validationError');
            error.hidden = false;
            return;
        }
        saveButton.disabled = true;
        saveButton.classList.add('is-loading');
        updateHourlyRate(place.id, { pricePerHour: hourly, pricePer3Hours: threeHours, pricePerDay: day })
            .then(() => {
            overlay.remove();
            loadHourlyRatesContent(ctx, content);
        })
            .catch((err) => {
            if (isUnauthorized(err)) {
                overlay.remove();
                ctx.showLogin();
                return;
            }
            error.textContent = t('admin.hourlyPayment.modal.saveError');
            error.hidden = false;
            saveButton.disabled = false;
            saveButton.classList.remove('is-loading');
        });
    });
    modal.append(closeButton, title, form);
    overlay.appendChild(modal);
    ctx.section.appendChild(overlay);
}
